import { Link, usePage } from "@inertiajs/react"
import { Inbox as InboxIcon, Voicemail } from "lucide-react"
import { useState } from "react"

import { type Channel, channelIcons } from "@/components/conversation-channel"
import { Empty } from "@/components/ui/empty"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"
import {
  homePath,
  inboxMessageReadPath,
  inboxPath,
  inboxReadAllPath,
} from "@/routes"

// IMP-519: the nav Inbox — the unread queue of messages conversations
// produce when a caller asks for the owner. Messages and only messages:
// leads, bookings and faults have their own surfaces and none of them are
// read-once artifacts.

interface Message {
  uid: string
  channel: Channel
  caller_name: string | null
  caller_phone: string | null
  caller_email: string | null
  body: string
  taken_at: string
  read: boolean
  conversation_id: string | null
}

function callerLabel(message: Message): string {
  return message.caller_name ?? message.caller_phone ?? "Unknown caller"
}

// A second identifier line when it adds information — never prints the same
// identifier twice.
function callerDetail(message: Message): string | null {
  if (!message.caller_name) return message.caller_email
  return message.caller_phone ?? message.caller_email
}

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  )
}

function patch(path: string) {
  void fetch(path, {
    method: "PATCH",
    headers: { "X-CSRF-Token": csrfToken() },
    keepalive: true,
  })
}

export function InboxNav() {
  const sharedProps = usePage().props as { unread_messages_count?: number }
  const [messages, setMessages] = useState<Message[] | null>(null)
  const [open, setOpen] = useState(false)
  // The badge follows the server count until the popover changes it locally;
  // the next Inertia visit hands authority back to the server.
  const [unreadOverride, setUnreadOverride] = useState<number | null>(null)
  const unreadCount = unreadOverride ?? sharedProps.unread_messages_count ?? 0

  function onOpenChange(next: boolean) {
    setOpen(next)
    if (next && messages === null) {
      void fetch(inboxPath(), { headers: { Accept: "application/json" } })
        .then((response) => response.json())
        .then((data: { messages: Message[] }) => setMessages(data.messages))
    }
  }

  function markRead(message: Message) {
    if (message.read) return
    patch(inboxMessageReadPath(message.uid))
    setMessages(
      (current) =>
        current?.map((m) =>
          m.uid === message.uid ? { ...m, read: true } : m,
        ) ?? null,
    )
    setUnreadOverride(Math.max(0, unreadCount - 1))
  }

  function markAllRead() {
    patch(inboxReadAllPath())
    setMessages(
      (current) => current?.map((m) => ({ ...m, read: true })) ?? null,
    )
    setUnreadOverride(0)
  }

  return (
    <SidebarMenuItem>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <SidebarMenuButton
            tooltip={{ children: "Inbox" }}
            className="cursor-pointer"
          >
            <InboxIcon />
            <span>Inbox</span>
          </SidebarMenuButton>
        </PopoverTrigger>
        {unreadCount > 0 && (
          // White fill with a hairline border — a quantity, not an alert.
          <SidebarMenuBadge className="border-border bg-background text-muted-foreground rounded-full border px-2 py-0.5 text-xs font-medium">
            {unreadCount}
          </SidebarMenuBadge>
        )}
        <PopoverContent
          side="right"
          align="start"
          sideOffset={12}
          className="w-[26rem] max-w-[calc(100vw-2rem)] p-0"
        >
          <div className="flex items-center justify-between border-b px-4 py-3">
            <p className="text-sm font-semibold">Messages</p>
            <button
              type="button"
              onClick={markAllRead}
              disabled={unreadCount === 0}
              className="text-muted-foreground hover:text-foreground text-xs font-medium disabled:opacity-50"
            >
              Mark all read
            </button>
          </div>
          <div className="max-h-[70vh] divide-y overflow-y-auto">
            {messages === null ? (
              <div className="flex justify-center px-4 py-10">
                <Spinner className="size-4" />
              </div>
            ) : messages.length === 0 ? (
              <div className="px-4 py-10">
                <Empty
                  icon={<Voicemail className="h-5 w-5" />}
                  title="No messages"
                />
              </div>
            ) : (
              messages.map((message) => {
                const Icon = channelIcons[message.channel]
                const detail = callerDetail(message)
                const row = (
                  <>
                    <div className="bg-muted text-muted-foreground mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg">
                      <Icon className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span
                          className={cn(
                            "truncate text-sm",
                            message.read ? "font-medium" : "font-semibold",
                          )}
                        >
                          {callerLabel(message)}
                        </span>
                        {!message.read && (
                          <span className="size-1.5 shrink-0 self-center rounded-full bg-blue-500" />
                        )}
                        <span className="text-muted-foreground ml-auto shrink-0 text-xs tabular-nums">
                          {message.taken_at}
                        </span>
                      </div>
                      {detail && (
                        <p className="text-muted-foreground mt-0.5 text-xs tabular-nums">
                          {detail}
                        </p>
                      )}
                      {/* The full body, untruncated — the message is the point. */}
                      <p className="text-muted-foreground mt-1 text-sm">
                        {message.body}
                      </p>
                    </div>
                  </>
                )
                const rowClass =
                  "hover:bg-muted/40 flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors"

                // Opening lands on the conversation the message came from, in
                // the contact drawer; a message with no resolvable
                // conversation still marks read in place.
                return message.conversation_id ? (
                  <Link
                    key={message.uid}
                    href={homePath({ conversation: message.conversation_id })}
                    onClick={() => {
                      markRead(message)
                      setOpen(false)
                    }}
                    className={rowClass}
                  >
                    {row}
                  </Link>
                ) : (
                  <button
                    key={message.uid}
                    type="button"
                    onClick={() => markRead(message)}
                    className={rowClass}
                  >
                    {row}
                  </button>
                )
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </SidebarMenuItem>
  )
}
